import React, { useState } from "react";
import { Modal } from "./Modal";

export interface SchemaDataModalProps {
	show: boolean;
	onHide: () => void;
	title: string;
	/** The JSON schema object (or string). Displayed in the "Schema" tab. */
	schema: any;
	/** The data object (or string). Displayed in the "Data" tab. */
	data: any;
}

/**
 * Reusable modal that shows a togglable Schema / Data JSON viewer.
 */
const SchemaDataModal: React.FC<SchemaDataModalProps> = ({
	show,
	onHide,
	title,
	schema,
	data
}) => {
	const [viewMode, setViewMode] = useState<"schema" | "data">("schema");

	const formatContent = (value: any, fallback: string): string => {
		if (value === null || value === undefined) return fallback;
		if (typeof value === "string") return value;
		return JSON.stringify(value, null, 2);
	};

	return (
		<Modal show={show} onHide={onHide} centered size="lg">
			<Modal.Header closeButton>
				<Modal.Title>{title} {viewMode === "schema" ? "schema" : "data"} (JSON)</Modal.Title>
			</Modal.Header>
			<Modal.Body scrollable className="bg-slate-50">
				<div className="mb-3 flex gap-2">
					<button
						type="button"
						onClick={() => setViewMode("schema")}
						className={`rounded-full border px-3 py-1 text-xs font-semibold ${
							viewMode === "schema"
								? "border-slate-300 bg-white text-slate-800 shadow-sm"
								: "border-slate-200 bg-slate-100 text-slate-600 hover:border-slate-300"
						}`}
					>
						Schema
					</button>
					<button
						type="button"
						onClick={() => setViewMode("data")}
						className={`rounded-full border px-3 py-1 text-xs font-semibold ${
							viewMode === "data"
								? "border-slate-300 bg-white text-slate-800 shadow-sm"
								: "border-slate-200 bg-slate-100 text-slate-600 hover:border-slate-300"
						}`}
						disabled={data === null || data === undefined}
					>
						Data
					</button>
				</div>
				<div className="rounded-xl border border-slate-200 bg-white p-4">
					<pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-xs text-slate-800">
						{viewMode === "schema"
							? formatContent(schema, "Schema not available.")
							: formatContent(data, "No data loaded.")}
					</pre>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onHide}
					className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
				>
					Close
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default SchemaDataModal;
